import { useEffect, useRef, useState } from 'react';

import {
  ListingType,
  RentalManagerPropertyAddressVerificationWebApiHandler_verification,
} from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerPropertyAddressDetailsWebApiHandler_details } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import enterpriseUtils from '../../utils/enterpriseUtils';
import { isUnitNumberOverLimit, resolveUnitAlert } from '../../utils/propertyValidationUtils';

import { UNIT_ALERT, type UnitAlertValue } from '../../constants/AddPropertyConstants';
import { ErrorMessageConstants } from '../../constants/ErrorMessageConstants';
import PropertyConstants from '../../constants/PropertyConstants';

import { usePropertyLocationCheck } from '../usePropertyLocationCheck';

import type { Dispatch } from 'react';
import type { ValueOf } from 'type-fest';
import type {
  RentalManagerAuthCheckUserInfoWebApiDto,
  UserSingleFamilyEnterpriseContractsResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type {
  ListingErrorsReducerProps,
  ListingErrorsReducerState,
  ListingReducerProps,
} from '../../components/modals/AddPropertyModal/reducers';

const {
  PROPERTY_TYPES,
  PROPERTY_TYPES: { COMMUNITY, CONDO },
  PROPERTY_TYPES_BY_ENUM,
  PROPERTY_CODES_BY_TYPE,
} = PropertyConstants;

type PropertyTypes = ValueOf<typeof PROPERTY_TYPES>;

const useValidateListing = (
  listing: ListingReducerProps,
  setListing: Dispatch<{ type: keyof ListingReducerProps | 'UPDATE_ALL'; payload: unknown }>,
  errorObj: ListingErrorsReducerState,
  setErrorObj: Dispatch<ListingErrorsReducerProps>,
  setUnitAlert: (alert: UnitAlertValue | null) => void,
  setPropertyAlreadyPostedWarningMessage: (data: string | Array<JSX.Element | string>) => void,
  setIsImpreciseLocation?: (data: boolean) => void,
  user?: Pick<
    RentalManagerAuthCheckUserInfoWebApiDto,
    'isSingleFamilyEnterpriseCustomer' | 'isMultifamilyCustomer' | 'userManagementCompanyId' | 'isPostingPathAdmin'
  >,
  contractsResponse?: UserSingleFamilyEnterpriseContractsResponseWebDto,
) => {
  const hasAnyExpiredContract = contractsResponse?.hasAnyExpiredContract;
  const contracts = contractsResponse?.contracts || [];
  const [paidMultifamilyLocation, setPaidMultifamilyLocation] = useState(false);

  const { checkLocationRestriction: checkRestrictions } = usePropertyLocationCheck({
    user: user ?? undefined,
    setPropertyAlreadyPostedWarningMessage,
  });

  const { data: addressDetailsData } = useRentalManagerPropertyAddressDetailsWebApiHandler_details(
    { fullAddress: listing.address || '', unit: listing.unit || '' },
    { enabled: !!listing.address && !listing.propertyType },
  );

  const onChangePropertyTypeRef = useRef<(propertyTypeToShow: string) => void>(() => {});

  useEffect(() => {
    if (addressDetailsData?.propertyType && !listing.propertyType) {
      const propertyTypeToShow = PROPERTY_TYPES_BY_ENUM[addressDetailsData.propertyType];

      if (propertyTypeToShow) {
        setListing({ type: 'propertyType', payload: propertyTypeToShow });
        onChangePropertyTypeRef.current(propertyTypeToShow);
      }
    }
  }, [addressDetailsData, listing.propertyType, setListing]);

  const prevAddress = useRef<{
    address: string;
    unit: string;
    propertyTypeCode: number | null;
  }>({
    address: '',
    unit: '',
    propertyTypeCode: null,
  });

  const verifyIsValidAddress = async (options = { isPromptUnitNumberCheckRequired: false }) => {
    const { address, unit, propertyType } = listing;
    const { isPromptUnitNumberCheckRequired } = options;

    try {
      const propertyTypeCode = propertyType ? PROPERTY_CODES_BY_TYPE[propertyType as PropertyTypes] : null;
      const propertyTypeCodeParam = propertyTypeCode ?? undefined;

      const { isValidatedWithoutUnit, promptForUnit, propertyId, isInaccurateGeoPrecision, unitWithoutLabel } =
        await RentalManagerPropertyAddressVerificationWebApiHandler_verification({
          fullAddress: address || '',
          unit: unit || '',
          propertyTypeCode: propertyTypeCodeParam,
        });

      const resolvedAlert = resolveUnitAlert({ isValidatedWithoutUnit, promptForUnit }, unit || '');

      if (resolvedAlert === UNIT_ALERT.VALIDATED_WITHOUT_UNIT) {
        const notChanged =
          prevAddress.current.address === address &&
          prevAddress.current.unit === unit &&
          prevAddress.current.propertyTypeCode === propertyTypeCode;

        if (!notChanged) {
          setUnitAlert(UNIT_ALERT.VALIDATED_WITHOUT_UNIT);

          prevAddress.current.address = address || '';
          prevAddress.current.unit = unit || '';
          prevAddress.current.propertyTypeCode = propertyTypeCode;

          return false;
        }

        prevAddress.current.address = '';
        prevAddress.current.unit = '';
        prevAddress.current.propertyTypeCode = null;
      }

      if (isPromptUnitNumberCheckRequired && resolvedAlert === UNIT_ALERT.PROMPT_FOR_UNIT) {
        setUnitAlert(UNIT_ALERT.PROMPT_FOR_UNIT);

        return false;
      }

      setListing({ type: 'listingAlias', payload: propertyId.toString() });
      setIsImpreciseLocation?.(isInaccurateGeoPrecision);

      // use normalized unit value from verification response
      if (unitWithoutLabel) {
        setListing({ type: 'unit', payload: unitWithoutLabel });
      }

      // Verification can echo back a normalized unit longer than the limit — stop here so the
      // field shows the error instead of the server rejecting it under Street address.
      if (isUnitNumberOverLimit(unitWithoutLabel || unit)) {
        return false;
      }

      return true;
    } catch ({ message }) {
      setErrorObj({ type: 'address', payload: message });
      return false;
    }
  };

  const verifyIsFormCompleted = () => {
    const { address, propertyType, numUnits, unit } = listing;
    const mustSetCustomerId = user
      ? enterpriseUtils.getMustSetCustomerId(
          listing,
          user,
          paidMultifamilyLocation,
          hasAnyExpiredContract,
          contracts.length > 0,
        )
      : false;
    const customerErrorMessage =
      contracts.length === 0 ? ErrorMessageConstants.SINGLE_FAMILY_NO_ACTIVE_CONTRACT : 'Select an active contract';
    const newErrorObject = {
      ...errorObj,
      address: address === '' ? 'Enter an address' : errorObj.address || '',
      propertyType: propertyType === '' ? 'Enter a property type' : errorObj.propertyType || '',
      numUnits: numUnits === '' && propertyType === COMMUNITY ? 'Enter number of units' : errorObj.numUnits || '',
      customerId: mustSetCustomerId ? customerErrorMessage : errorObj.customerId || '',
    };

    setErrorObj({ type: 'listing', payload: newErrorObject });

    // The unit field renders its own over-limit message via CharacterCountFormHelp, so this
    // only has to block submission.
    return Object.values(newErrorObject).every((key) => key === '') && !isUnitNumberOverLimit(unit);
  };

  const verifyCanSubmitForm = async (
    options = {
      isPromptUnitNumberCheckRequired: false,
    },
  ) => {
    const isFormCompleted = verifyIsFormCompleted();
    if (!isFormCompleted) {
      return false;
    }
    const isValidAddress = await verifyIsValidAddress(options);
    return isValidAddress;
  };

  const handleCheckLocationRestriction = async (
    onChangePropertyType: (propertyTypeToShow: string) => void,
    keyName?: keyof ListingReducerProps,
    newValue: boolean | string = '',
  ) => {
    onChangePropertyTypeRef.current = onChangePropertyType;

    const newListing = { ...listing };
    if (keyName) {
      // @ts-expect-error - newValue can be boolean or string, property types vary by key
      newListing[keyName] = newValue;
    }

    const { address, isRoomForRent, propertyType, unit } = newListing;

    if (!address) return;

    try {
      const result = await checkRestrictions({
        address,
        unit,
        propertyType: propertyType as string | undefined,
        listingType: isRoomForRent ? ListingType.ROOM : ListingType.RENTAL,
      });

      if (!result) {
        if (listing.duplicateAccountListingAlias) {
          setListing({ type: 'duplicateAccountListingAlias', payload: null });
        }
        return;
      }

      setListing({ type: 'duplicateAccountListingAlias', payload: result.duplicateAccountListingAlias });
      if (result.paidMultifamilyLocation !== paidMultifamilyLocation) {
        setPaidMultifamilyLocation(result.paidMultifamilyLocation);
      }

      if (result.errorMessage) {
        setErrorObj({ type: 'address', payload: result.errorMessage });

        // if user sees the ErrorMessageConstants.LOCATION_RESTRICTION.MF_REQUIRED message returned by
        // getLocationRestrictionErrorMessage(), then auto set the property to be community,
        // unless the user manually input a unit, which would make it a condo.
        if (result.paidMultifamilyLocation) {
          if (!newListing.propertyType) {
            const propertyTypeToShow = unit ? CONDO : COMMUNITY;
            setListing({ type: 'propertyType', payload: propertyTypeToShow });
            onChangePropertyType(propertyTypeToShow);
          }
          return;
        }
      }

      return true;
    } finally {
      if (keyName) setListing({ type: keyName, payload: newValue });
    }
  };

  return { verifyCanSubmitForm, handleCheckLocationRestriction };
};

export { useValidateListing };
